<template>
  <ValidationObserver ref="observer" class="transition duration-500 ease-in-out transform w-full"
                      style="transition: all 1s !important"
                      tag="form" @submit.prevent="submit()">
    <div class="w-full flex cpd-activity-form__shell">
      <v-card class="mx-auto w-full" flat max-height="auto" max-width="1000">
        <div class="cpd-activity-form__section-title">
          <span>CPD Activity Information</span>
        </div>

        <div class="flex flex-wrap cpd-activity-form__grid">
          <div class="w-full md:w-1/2 px-3 pt-3 md:mb-0 required">
            <label class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              ">CPD Activity Type:</label>
            <validation-provider v-slot="{ errors }" rules="required">
              <v-select v-model="formValues.programTypeId" :clearable="clearable" :elevation="1" :items="PROGRAM_TYPES"
                        :loading="programTypeLoading" :searchable="searchable" dense item-text="name" item-value="id"
                        label="cpd activity types" solo></v-select>

              <span class="text-gray-600 text-xs italic text-red-700">{{
                  errors[0]
                }}</span>
            </validation-provider>
            <br/>
          </div>
          <div v-if="showTrainingModality" class="w-full md:w-1/2 px-3 pt-3 md:mb-0 required">
            <label class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              ">CPD Activity:
            </label>
            <validation-provider v-slot="{ errors }" rules="required">
              <v-select v-model="formValues.categoryId" :clearable="clearable" :elevation="1" :items="CATEGORIES"
                        :loading="cpdActivityLoading" :searchable="searchable" dense item-text="name" item-value="id"
                        label="cpd activity" solo></v-select>

              <span class="text-gray-600 text-xs italic text-red-700">{{
                  errors[0]
                }}</span>
            </validation-provider>
            <br/>
          </div>
          <div v-if="showCourses" class="w-full md:w-1/2 px-3 md:mb-0">
            <label class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              ">Course Title</label>
            <v-autocomplete v-model="formValues.course" :clearable="clearable" :items="COURSES"
                            :loading="coursesLoading" :searchable="searchable"
                            class="
                  shadow
                  appearance-none
                  border
                  rounded
                  w-full
                  py-2
                  px-3
                  text-gray-700
                  leading-tight
                  focus:outline-none
                  focus:shadow-outline
                " item-text="courseName" return-object>
            </v-autocomplete>

            <br/>
          </div>

          <div v-if="showCoursesText" class="w-full md:w-1/2 px-3 mb-6">
            <label class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              ">Course Title</label>
            <input id="courseTitle" v-model="formValues.courseTitleCat2" class="
                  shadow
                  appearance-none
                  border
                  rounded
                  w-full
                  py-2
                  px-3
                  text-gray-700
                  leading-tight
                  focus:outline-none
                  focus:shadow-outline
                " name placeholder="Activity Title"
                   type="text"/>
          </div>

          <div
              v-if="showCat2RequirementRole"
              class="w-full md:w-1/2 px-3 mb-6"
              :class="{ required: cat2RoleRules === 'required' }"
          >
            <label class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              ">Default Role</label>
            <validation-provider v-slot="{ errors }" :rules="cat2RoleRules">
              <v-select
                  v-model="formValues.cat2RequirementId"
                  :clearable="clearable"
                  :elevation="1"
                  :items="CAT2_REQUIREMENTS"
                  :loading="cat2RequirementLoading"
                  dense
                  item-text="role"
                  item-value="id"
                  label="default role"
                  solo
              ></v-select>
              <span class="text-gray-600 text-xs italic text-red-700">{{ errors[0] }}</span>
            </validation-provider>
          </div>

          <div
              v-if="showCat2MultiplierValue"
              class="w-full md:w-1/2 px-3 mb-6"
              :class="{ required: cat2MultiplierRules.length > 0 }"
          >
            <label class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              ">{{ defaultCat2MultiplierLabel }}</label>
            <validation-provider v-slot="{ errors }" :rules="cat2MultiplierRules">
              <input
                  id="cat2MultiplierValue"
                  v-model.number="formValues.cat2MultiplierValue"
                  class="
                  shadow
                  appearance-none
                  border
                  rounded
                  w-full
                  py-2
                  px-3
                  text-gray-700
                  leading-tight
                  focus:outline-none
                  focus:shadow-outline
                "
                  name
                  :placeholder="defaultCat2MultiplierLabel"
                  type="number"
                  min="0"
              />
              <span class="text-gray-600 text-xs italic text-red-700">{{ errors[0] }}</span>
            </validation-provider>
          </div>
          <div class="w-full md:w-1/2 px-3 mb-6 required">
            <label class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              ">{{ ceuLabel }}:
            </label>
            <validation-provider v-slot="{ errors }" rules="required|min_value:0|max_value:15">
              <input id="ceu" v-model.number="formValues.ceu" class="
                  shadow
                  appearance-none
                  border
                  rounded
                  w-full
                  py-2
                  px-3
                  text-gray-700
                  leading-tight
                  focus:outline-none
                  focus:shadow-outline
                "
                     :disabled="isCeuLocked"
                     name :placeholder="ceuLabel" type="number"/>
              <span class="text-gray-600 text-xs italic text-red-700">{{
                  errors[0]
                }}</span>
            </validation-provider>
          </div>
          <div v-if="showCeu" class="w-full md:w-1/2 px-3 pt-3 mb-6 required">
            <label class="
                  block
                  uppercase
                  tracking-wide
                  text-gray-700 text-xs
                  font-bold
                  mb-2
                ">Course Level</label>
            <validation-provider v-slot="{ errors }" rules="required">
              <v-select v-model="formValues.courseLevelId" :clearable="clearable" :elevation="1" :items="COURSE_LEVELS"
                        :loading="courseLevelLoading" :searchable="searchable" dense item-text="name" item-value="id"
                        label="course levels" solo></v-select>

              <span class="text-gray-600 text-xs italic text-red-700">{{
                  errors[0]
                }}</span>
            </validation-provider>

            <br/>
          </div>
          <div v-if="showCeu" class="w-full md:w-1/2 px-3 mb-6 required">
            <label class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              ">Passing Score:
            </label>
            <validation-provider v-slot="{ errors }" rules="required|min_value:0|max_value:100">
              <input id="cat2PassingScore" v-model="formValues.cat2PassingScore" class="
                  shadow
                  appearance-none
                  border
                  rounded
                  w-full
                  py-2
                  px-3
                  text-gray-700
                  leading-tight
                  focus:outline-none
                  focus:shadow-outline
                " name placeholder="passing score"
                     type="number"/>
              <span class="text-gray-600 text-xs italic text-red-700">{{
                  errors[0]
                }}</span>
            </validation-provider>
          </div>

          <div v-if="showCourseLevel" class="w-full md:w-1/2 px-3 mb-6 required">
            <label class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              ">Course Level:
            </label>
            <validation-provider v-slot="{ errors }" rules="required">
              <v-select
                  v-model="formValues.courseLevelId"
                  :clearable="false"
                  :disabled="courseLevelOptions.length <= 1"
                  :elevation="1"
                  :items="courseLevelOptions"
                  dense
                  hide-details
                  item-text="name"
                  item-value="id"
                  label="course levels"
                  solo
              ></v-select>
              <span class="text-gray-600 text-xs italic text-red-700">{{ errors[0] }}</span>
            </validation-provider>
          </div>
          <div v-if="showPassingScore" class="w-full md:w-1/2 px-3 mb-6 required">
            <label class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              ">Passing Score:
            </label>
            <input id="cat2PassingScore" v-model="passingScoreValue" class="
                  shadow
                  appearance-none
                  border
                  rounded
                  w-full
                  py-2
                  px-3
                  text-gray-700
                  leading-tight
                  focus:outline-none
                  focus:shadow-outline
                " disabled placeholder="passing score"
                   type="number"/>
          </div>
          <div class="w-full md:w-1/2 px-3 pt-3 md:mb-0">
            <label class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              ">CPD Provider</label>
            <v-autocomplete v-model="formValues.cpdProviderId" :clearable="clearable" :elevation="1"
                            :items="CPD_PROVIDERS"
                            :loading="cpdProviderLoading" :searchable="searchable" dense item-text="trainingCenterName"
                            item-value="id"
                            label="cpd providers" solo
                            :disabled="Boolean(formValues?.cpdProviderId)"
            ></v-autocomplete>
            <br/>
          </div>
          <div v-if="showActivityDelivery" class="w-full md:w-1/2 px-3 pt-3 md:mb-0">
            <label class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              ">Activity Delivery Modality</label>
            <validation-provider v-slot="{ errors }" rules="required">
              <v-select v-model="formValues.activityDeliveryId" :clearable="clearable" :elevation="1"
                        :items="ACTIVITY_DELIVERIES"
                        :loading="activityDelivLoading" :searchable="searchable" dense item-text="name"
                        item-value="id"
                        label="activity delivery modalities" solo></v-select>

              <span class="text-gray-600 text-xs italic text-red-700">{{
                  errors[0]
                }}</span>
            </validation-provider>
          </div>
          <div v-if="showTrainingModality" class="w-full md:w-1/2 px-3 pt-3 md:mb-0">
            <label class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              ">Default Certificate Reason:
            </label>
            <v-select
                v-model="formValues.certificateReasonId"
                :clearable="true"
                :elevation="1"
                :items="CERTIFICATE_REASON"
                :loading="certReasonLoading"
                :searchable="searchable"
                dense
                item-text="name"
                item-value="id"
                label="default certificate reason"
                solo
            ></v-select>
            <br/>
          </div>
        </div>

        <div class="cpd-activity-form__section-title">
          <span>Activity Information</span>
        </div>

        <div class="flex flex-wrap cpd-activity-form__grid">
          <div class="w-full md:w-1/2 px-3 mb-6 required">
            <label class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              ">Hosting Institution</label>
            <validation-provider v-slot="{ errors }" rules="required">
              <input id="facilitatedby" v-model="formValues.facilitatedBy" class="
                  shadow
                  appearance-none
                  border
                  rounded
                  w-full
                  py-2
                  px-3
                  text-gray-700
                  leading-tight
                  focus:outline-none
                  focus:shadow-outline
                " name placeholder="hosting institution"
                     type="text"/>
              <span class="text-gray-600 text-xs italic text-red-700">{{
                  errors[0]
                }}</span>
            </validation-provider>
          </div>
          <div class="w-full md:w-1/2 px-3 mb-6 md:mb-0 required">
            <label class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              ">Participants Number</label>
            <validation-provider v-slot="{ errors }" rules="required|min_value:1">
              <input id="participantsNumber" v-model="formValues.participantsNumber" class="
                  shadow
                  appearance-none
                  border
                  rounded
                  w-full
                  py-2
                  px-3
                  text-gray-700
                  leading-tight
                  focus:outline-none
                  focus:shadow-outline
                " name
                     placeholder="participants number(only numeric values)" type="number"/>
              <span class="text-gray-600 text-xs italic text-red-700">{{
                  errors[0]
                }}</span>
            </validation-provider>
            <br/>
          </div>
          <div v-if="showTrainersNumber" class="w-full md:w-1/2 px-3 mb-6 md:mb-0">
            <label class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              ">Trainers Number</label>
<!--            <validation-provider v-slot="{ errors }" rules="required|min_value:1">-->
              <input id="trainersNumber" v-model="formValues.trainersNumber" class="
                  shadow
                  appearance-none
                  border
                  rounded
                  w-full
                  py-2
                  px-3
                  text-gray-700
                  leading-tight
                  focus:outline-none
                  focus:shadow-outline
                " name
                     placeholder="trainers number(only numeric values)" type="number"/>
<!--              <span class="text-gray-600 text-xs italic text-red-700">{{-->
<!--                  errors[0]-->
<!--                }}</span>-->
<!--            </validation-provider>-->
            <br/>
          </div>
        </div>

        <div class="cpd-activity-form__section-title">
          <span>Activity Duration</span>
        </div>

        <div class="flex flex-wrap cpd-activity-form__grid">
          <div class="w-full md:w-1/2 px-3 mb-6 md:mb-0 required">
            <label class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              ">Start Date</label>
            <validation-provider v-slot="{ errors }" rules="required">
              <v-menu ref="startMenu" v-model="startMenu" :close-on-content-click="false" max-width="290px"
                      min-width="290px" offset-y transition="scale-transition">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="formValues.startDate" :clearable="true" label="Pick start date"
                                persistent-hint prepend-icon="mdi-calendar" v-bind="attrs" v-on="on"></v-text-field>
                </template>
                <v-date-picker
                    ref="startDate" v-model="formValues.startDate"
                    no-title scrollable
                    @input="startMenu = false"
                ></v-date-picker>
              </v-menu>
              <span class="text-gray-600 text-xs italic text-red-700">{{
                  errors[0]
                }}</span>
              <span
                  v-if="durationDays && formValues.startDate"
                  class="text-gray-500 text-xs italic"
              >
                Minimum end date: {{ minEndDate }} (start date + {{ durationDays }} days)
              </span>
            </validation-provider>
            <br/>
          </div>

          <div class="w-full md:w-1/2 px-3 mb-6 md:mb-0 required">
            <label class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              ">End Date</label>
            <validation-provider v-slot="{ errors }" :rules="endDateRules">
              <v-menu ref="endMenu" v-model="endMenu" :close-on-content-click="false" :return-value.sync="date"
                      min-width="290px" offset-y transition="scale-transition"
                      :disabled="!formValues.startDate">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="formValues.endDate" :clearable="true" label="Pick end date"
                                prepend-icon="event"
                                :disabled="!formValues.startDate"
                                readonly v-bind="attrs" v-on="on"></v-text-field>
                </template>
                <v-date-picker
                    v-model="formValues.endDate" :min="minEndDate || formValues.startDate" no-title
                    scrollable @input="endMenu = false"
                ></v-date-picker>
              </v-menu>
              <span class="text-gray-600 text-xs italic text-red-700">{{
                  errors[0]
                }}</span>
            </validation-provider>
            <br/>
          </div>

        </div>

        <div class="cpd-activity-form__section-title">
          <span>Program Event Information</span>
        </div>

        <div class="flex flex-wrap cpd-activity-form__grid">

          <div class="w-full md:w-1/2 px-3 mb-6" required>
            <label class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              ">Pick event color</label>
            <validation-provider v-slot="{ errors }" rules="required">
              <v-text-field v-model="color" class="ma-0 pa-0" hide-details solo>
                <template v-slot:append>
                  <v-menu v-model="colorMenu" :close-on-content-click="false" nudge-bottom="105" nudge-left="16" top>
                    <template v-slot:activator="{ on }">
                      <div :style="swatchStyle" v-on="on"/>
                    </template>
                    <v-card>
                      <v-card-text class="pa-0">
                        <v-color-picker v-model="color" flat/>
                      </v-card-text>
                    </v-card>
                  </v-menu>
                </template>
              </v-text-field>
              <span class="text-gray-600 text-xs italic text-red-700">{{
                  errors[0]
                }}</span>
            </validation-provider>
          </div>

          <div class="w-full md:w-1/2 px-3 mb-6">
            <label class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              ">Expected Outcome</label>
            <validation-provider v-slot="{ errors }">
              <textarea v-model="eventData.expectedOutcome" class="event-outcome-field
                  shadow
                  appearance-none
                  border
                  rounded
                  w-full
                  py-2
                  px-3
                  text-gray-700
                  leading-tight
                  focus:outline-none
                  focus:shadow-outline
                " placeholder="Enter Expected Outcome." rows="1"></textarea>
              <span class="text-gray-600 text-xs italic text-red-700">{{
                  errors[0]
                }}</span>
            </validation-provider>
          </div>

          <div v-if="false" class="w-full md:w-1/2 px-3 mb-6 mb-0 required">
            <label class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              ">Funded By:</label>
            <validation-provider v-slot="{ errors }" rules="required">
              <select v-model="eventData.fundedById" class="
                  minimal
                  shadow
                  appearance-none
                  border
                  rounded
                  w-full
                  py-2
                  px-3
                  text-gray-700
                  leading-tight
                  focus:outline-none
                  focus:shadow-outline
                ">
                <option v-for="item in FUNDED_BY" :key="item.id" :value="item.id">
                  {{ item.name }}
                </option>
              </select>

              <span class="text-gray-600 text-xs italic text-red-700">{{
                  errors[0]
                }}</span>
            </validation-provider>

            <br/>
          </div>
          <div class="w-full px-3 mb-6">
            <div class="flex items-center justify-between mb-3">
              <label class="block uppercase tracking-wide text-gray-700 text-xs font-bold">Funders</label>
              <v-btn color="primary" outlined small type="button" @click="addFunder">
                <v-icon left small>mdi-plus</v-icon>Add Funder
              </v-btn>
            </div>
            <div v-if="!eventData.funders || !eventData.funders.length" class="text-gray-500 text-sm">
              No funders added.
            </div>
            <div
                v-for="(funder, index) in eventData.funders"
                :key="funder.id || `new-funder-${index}`"
                class="flex flex-wrap items-start funder-row"
            >
              <div class="w-full md:w-1/2 pr-3 required">
                <validation-provider v-slot="{ errors }" rules="required">
                  <v-autocomplete
                      v-model="funder.organizationId"
                      :items="availableFunders(index)"
                      item-text="name"
                      item-value="id"
                      label="Funder"
                      outlined
                      dense
                  />
                  <span class="text-gray-600 text-xs italic text-red-700">{{ errors[0] }}</span>
                </validation-provider>
              </div>
              <div class="w-full md:w-1/3 px-3 required">
                <validation-provider v-slot="{ errors }" rules="required|min_value:0">
                  <v-text-field
                      v-model="funder.fundAmount"
                      label="Fund Amount"
                      type="number"
                      min="0"
                      outlined
                      dense
                  />
                  <span class="text-gray-600 text-xs italic text-red-700">{{ errors[0] }}</span>
                </validation-provider>
              </div>
              <div class="w-full md:w-1/6 pt-1 text-right">
                <v-btn icon color="red" type="button" @click="removeFunder(index)">
                  <v-icon>mdi-delete-outline</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
          <div class="w-full md:w-1/2 px-3 mb-6">
            <label class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">Estimated Cost:
            </label>
            <validation-provider v-slot="{ errors }" rules="required|min_value:0">
              <input
                  id="totalCost"
                  v-model="eventData.totalCost"
                  class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  name placeholder="Total cost" type="number"/>
              <span class="text-gray-600 text-xs italic text-red-700">{{
                  errors[0]
                }}</span>
            </validation-provider>
          </div>
          <div class="w-full md:w-1/2 px-3 mb-6">
            <label class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">Collaborator:</label>
            <input
                id="totalCost"
                v-model="formValues.collaborator"
                class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="collaborators" type="text"/>
          </div>
        </div>

        <div class="flex flex-wrap cpd-activity-form__actions">
          <div class="w-full">
            <button class="cpd-activity-form__submit" type="button" @click="submit()">
              {{
                formValues.editMode ? "Update CPD Activity" : "Add CPD Activity"
              }}
            </button>
          </div>
        </div>
      </v-card>
    </div>
  </ValidationObserver>
</template>

<script>
import {
  getActivityDeliveries,
  getCategories,
  getCertReason,
  getCourseLevel,
  getCourses,
  getFoundedBy,
  getProgramTypes,
  getUser
} from "@/utils/helper";

import {extend, ValidationObserver, ValidationProvider} from "vee-validate";
import {max_value, min_value, required} from "vee-validate/dist/rules";
import {TrainingCenterService} from '../TrainingCenter/training_center.service'
import {ProgramEventService} from "./../ProgramEvents/programevent.service";
import {LookupService} from "@/common/api.service";

const CPD_ACTIVITY_CATEGORY_ORDER = [11, 20, 22, 19, 18];

const sortCpdActivityCategories = (categories = []) => {
  const priorityById = new Map(
      CPD_ACTIVITY_CATEGORY_ORDER.map((categoryId, index) => [categoryId, index])
  );

  return [...categories].sort((left, right) => {
    const leftPriority = priorityById.has(Number(left?.id))
        ? priorityById.get(Number(left.id))
        : CPD_ACTIVITY_CATEGORY_ORDER.length;
    const rightPriority = priorityById.has(Number(right?.id))
        ? priorityById.get(Number(right.id))
        : CPD_ACTIVITY_CATEGORY_ORDER.length;

    if (leftPriority !== rightPriority) return leftPriority - rightPriority;
    return String(left?.name || "").localeCompare(String(right?.name || ""));
  });
};

extend("required", {
  ...required,
  message: "This field is required",
});

extend("min_end_date", {
  params: ["min"],
  validate(value, {min}) {
    if (!value || !min) return true;
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      const [year, month, day] = String(dateStr).split("-").map(Number);
      if (!year || !month || !day) return null;
      return new Date(year, month - 1, day);
    };
    const valueDate = parseDate(value);
    const minDate = parseDate(min);
    if (!valueDate || !minDate) return true;
    return valueDate.getTime() >= minDate.getTime();
  },
  message: "End date must be on or after {min}.",
});

extend("min_value", {
  ...min_value,
  message: "Invalid Value",
});
extend("max_value", {
  ...max_value,
  message: "Invalid Value",
});

export default {
  props: ["program", "changeProgramTypeNo", "changeCategoryNo"],
  components: {
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      formValues: {
        cpdActivityTitle: "",
        cpdProviderId: null,
        programTypeId: "",
        facilitatedBy: "",
        participantsNumber: "",
        trainersNumber: 0,
        categoryId: null,
        startDate: "",
        endDate: "",
        trainingTitle: "",
        activityDeliveryId: null,
        editMode: false,
        course: null,
        courseTitleCat2: "",
        ceu: null,
        cat2PassingScore: 0,
        collaborator: "",
        certificateReasonId: null,
        courseLevelId: null,
        cat2RequirementId: null,
        cat2Role: null,
        cat2Multiplier: null,
        cat2MultiplierValue: null,
      },
      eventData: {
        start: null,
        end: null,
        programId: null,
        trainingCenterId: null,
        color: null,
        expectedOutcome: null,
        totalCost: 0,
        fundedById: null,
        funders: [],
        editMode: false,
      },
      color: "",
      showProgress: false,
      PROGRAM_TYPES: [],
      COURSES: [],
      showTrainingModality: false,
      CATEGORIES: [],
      date: new Date().toISOString().substr(0, 10),
      ACTIVITY_DELIVERIES: [],
      showCourses: false,
      showCoursesText: false,
      showCeu: false,
      showCourseLevel: false,
      showPassingScore: false,
      showTrainersNumber: false,
      courseLevelOptions: [],
      passingScoreValue: "",
      // showTrainingLevel: false,
      showActivityDelivery: false,
      startMenu: false,
      endMenu: false,
      modal: false,
      min: "",
      max: "",
      mutableProgramTypeNo: this.changeProgramTypeNo,
      mutableCategoryNo: this.changeCategoryNo,
      CPD_PROVIDERS: [],
      programTypeLoading: false,
      clearable: true,
      searchable: true,
      cpdActivityLoading: false,
      coursesLoading: false,
      cpdProviderLoading: false,
      activityDelivLoading: false,
      colorMenu: false,
      FUNDED_BY: [],
      ORGANIZATIONS: [],

      COURSE_LEVELS: [],
      courseLevelLoading: false,
      courseCat:null,
      CERTIFICATE_REASON: [],
      certReasonLoading: false,

      CAT2_REQUIREMENTS: [],
      cat2RequirementLoading: false,
    };
  },
  async beforeMount() {
    this.FUNDED_BY = await getFoundedBy();
    const organizationResponse = await LookupService.getOrganizations("Donor");
    this.ORGANIZATIONS = organizationResponse?.data?.rows || [];
  },
  computed: {
    swatchStyle() {
      const {color, colorMenu} = this;
      return {
        backgroundColor: color || "#ffffff",
        border: color ? "none" : "1px dashed #94a3b8",
        cursor: "pointer",
        height: "30px",
        width: "30px",
        borderRadius: colorMenu ? "50%" : "4px",
        transition: "border-radius 200ms ease-in-out",
      };
    },
    durationDays() {
      const levelId = this.formValues.courseLevelId;
      const selected =
          this.courseLevelOptions.find((level) => level.id === levelId) || null;
      const duration = selected?.courseDuration ?? this.formValues?.course?.courseDuration ?? null;
      const numericDuration = Number(duration);
      return Number.isFinite(numericDuration) && numericDuration > 0 ? numericDuration : null;
    },
    minEndDate() {
      const startDate = this.formValues.startDate;
      if (!startDate) return null;
      if (!this.durationDays) return startDate;
      return this.addDaysToDate(startDate, this.durationDays);
    },
    endDateRules() {
      return {
        required: true,
        min_end_date: this.minEndDate || null,
      };
    },
    isCeuLocked() {
      if (this.courseCat === "CAT1") return true;
      if (this.courseCat !== "CAT2") return false;
      // In edit mode, allow legacy CAT2 activities (no role saved) to keep manual CEU.
      if (this.formValues?.editMode && !this.formValues?.cat2RequirementId) return false;
      return true;
    },
    showCat2RequirementRole() {
      return this.courseCat === "CAT2" && this.showCoursesText;
    },
    selectedCat2Requirement() {
      if (!this.formValues?.cat2RequirementId) return null;
      return (
          this.CAT2_REQUIREMENTS.find(
              (r) => Number(r.id) === Number(this.formValues.cat2RequirementId)
          ) ||
          null
      );
    },
    showCat2MultiplierValue() {
      return this.showCat2RequirementRole && !!this.selectedCat2Requirement?.multiplier;
    },
    cat2MultiplierLabel() {
      const raw = (this.selectedCat2Requirement?.multiplier || "").toString().trim();
      if (!raw) return "Multiplier Value";
      // Most values are like "Per Hour", "Per 1 Hour", "Per Presentation"
      return raw
          .replace(/^Per\\s+/i, "")
          .replace(/^\\d+\\s+/i, "")
          .trim() || "Multiplier Value";
    },
    defaultCat2MultiplierLabel() {
      return `Default ${this.cat2MultiplierLabel}`;
    },
    ceuLabel() {
      return this.courseCat === "CAT2" ? "Default CEU" : "CEU";
    },
    cat2RoleRules() {
      if (!this.showCat2RequirementRole) return "";
      if (!this.formValues?.editMode) return "required";
      // Backward compatible: allow editing legacy CAT2 activities that already have a CEU saved.
      return this.formValues?.ceu === null || this.formValues?.ceu === undefined ? "required" : "";
    },
    cat2MultiplierRules() {
      return this.showCat2MultiplierValue ? "required|min_value:0" : "";
    },
  },
  async created() {
    await this.initiateLookups();
    this.COURSE_LEVELS = await getCourseLevel();
    if (this.program != null) {
      this.formValues = this.program;
      if (this.program.program_courses.length > 0) {
        this.formValues.course = this.program.program_courses[0].course
      }

      this.formValues.editMode = true;
      const res = await ProgramEventService.getEventByCPDActivity(
          this.formValues.id ? this.formValues.id : null,
      );

      if (res.status === 200) {
        this.loading = false;
        this.color = res.data[0].color
        this.eventData = res.data[0];
      } else {
        this.loading = false;
        this.$toast.error(
            "There was a problem fetching Event, Please try Again!"
        );
      }
    }
  },
  methods: {
    addFunder() {
      if (!Array.isArray(this.eventData.funders)) {
        this.$set(this.eventData, "funders", []);
      }
      this.eventData.funders.push({organizationId: null, fundAmount: null});
    },
    removeFunder(index) {
      this.eventData.funders.splice(index, 1);
    },
    availableFunders(currentIndex) {
      const selected = new Set(
          (this.eventData.funders || [])
              .filter((_, index) => index !== currentIndex)
              .map((funder) => Number(funder.organizationId))
              .filter(Boolean)
      );
      return this.ORGANIZATIONS.filter((organization) => !selected.has(Number(organization.id)));
    },
    formatDate(value) {
      if (!(value instanceof Date)) return null;
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, "0");
      const day = String(value.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    addDaysToDate(dateStr, days) {
      if (!dateStr) return null;
      const [year, month, day] = String(dateStr).split("-").map(Number);
      if (!year || !month || !day) return null;
      const date = new Date(year, month - 1, day);
      date.setDate(date.getDate() + Number(days));
      return this.formatDate(date);
    },
    ensureEndDateMin() {
      const minEnd = this.minEndDate;
      if (!minEnd) return;
      const endDate = this.formValues.endDate;
      if (!endDate) {
        this.formValues.endDate = minEnd;
        return;
      }
      const [endY, endM, endD] = String(endDate).split("-").map(Number);
      const [minY, minM, minD] = String(minEnd).split("-").map(Number);
      if (!endY || !endM || !endD || !minY || !minM || !minD) return;
      const end = new Date(endY, endM - 1, endD).getTime();
      const min = new Date(minY, minM - 1, minD).getTime();
      if (end < min) {
        this.formValues.endDate = minEnd;
      }
    },
    normalizeCourseLevels(course) {
      const rawLevels = Array.isArray(course?.courseLevels) ? course.courseLevels : [];
      if (rawLevels.length) {
        return rawLevels
            .map((level) => {
              const courseLevel = level.courseLevel || level.course_level;
              const id = level.courseLevelId || level.course_level_id || courseLevel?.id;
              if (!id) return null;
              return {
                id,
                name: courseLevel?.name || level.courseLevelName || level.course_level_name || `Level ${id}`,
                continualEducationUnit:
                    level.continualEducationUnit ?? level.continual_education_unit ?? course?.continualEducationUnit,
                courseDuration: level.courseDuration ?? level.course_duration ?? course?.courseDuration,
                passingScore: level.passingScore ?? level.passing_score ?? course?.passingScore,
              };
            })
            .filter(Boolean);
      }

      const fallbackId = course?.courseLevelId || course?.courseLevel?.id;
      if (!fallbackId) return [];
      return [
        {
          id: fallbackId,
          name: course?.courseLevel?.name || `Level ${fallbackId}`,
          continualEducationUnit: course?.continualEducationUnit ?? null,
          courseDuration: course?.courseDuration ?? null,
          passingScore: course?.passingScore ?? null,
        },
      ];
    },
    applyCourseLevel(level) {
      if (!level) {
        this.passingScoreValue = "";
        if (this.courseCat === "CAT1") {
          this.formValues.ceu = null;
        }
        return;
      }
      this.passingScoreValue = level.passingScore ?? "";
      if (this.courseCat === "CAT1") {
        this.formValues.ceu = level.continualEducationUnit ?? null;
      }
    },
    async initiateLookups() {
      this.programTypeLoading = true;
      this.cpdActivityLoading = true;
      this.coursesLoading = true;
      this.cpdProviderLoading = true;
      this.activityDelivLoading = true;
      this.PROGRAM_TYPES = await getProgramTypes();
      this.programTypeLoading = false;
      this.COURSES = await getCourses();
      this.coursesLoading = false;
      this.CATEGORIES = sortCpdActivityCategories(await getCategories());
      this.cpdActivityLoading = false;
      this.ACTIVITY_DELIVERIES = await getActivityDeliveries();
      this.activityDelivLoading = false;
      const cpdResponse = await TrainingCenterService.getAllTrainingCenters();
      this.CPD_PROVIDERS = cpdResponse.data.rows.filter((data) => {
        return data.isActive == true;
      });
      this.cpdProviderLoading = false;

      const user = getUser();
      this.formValues.cpdProviderId = this.formValues.cpdProviderId ?? user.data.cpdProviderId;

      this.certReasonLoading = true;
      const certResp = await getCertReason();
      this.CERTIFICATE_REASON = (certResp?.rows || []).filter((data) => {
        return data.isActive === true;
      });
      this.certReasonLoading = false;
    },
    async submit() {

      if (!(await this.$refs.observer.validate())) return;
      this.$refs.observer.validate();
      this.prepareEventData();
      this.formValues.event = this.eventData;

      this.$emit("registerOrUpdateProgram", this.formValues);

    },
    prepareEventData() {
      this.eventData.color = this.color || null;
      this.eventData.start = this.formValues.startDate;
      this.eventData.end = this.formValues.endDate;
      this.eventData.trainingCenterId = this.formValues.cpdProviderId;
    },
    async loadCat2Requirements(categoryId) {
      if (!categoryId) {
        this.CAT2_REQUIREMENTS = [];
        return;
      }

      this.cat2RequirementLoading = true;
      try {
        const res = await LookupService.getCatIIRequirementsByCategory(categoryId);
        const payload = res?.data ?? res ?? {};
        const rows = Array.isArray(payload) ? payload : payload?.rows ?? [];
        this.CAT2_REQUIREMENTS = Array.isArray(rows) ? rows : [];
      } catch (error) {
        this.CAT2_REQUIREMENTS = [];
      } finally {
        this.cat2RequirementLoading = false;
      }
    },
    applySelectedCat2Requirement() {
      const req = this.selectedCat2Requirement;
      if (!req) {
        this.formValues.cat2Role = null;
        this.formValues.cat2Multiplier = null;
        this.formValues.cat2MultiplierValue = null;
        // Don't wipe CEU when editing legacy CAT2 activities that were created before role support.
        if (!this.formValues?.editMode) {
          this.formValues.ceu = null;
        }
        return;
      }

      this.formValues.cat2Role = req.role || null;
      this.formValues.cat2Multiplier = req.multiplier || null;

      const baseCeu = Number(req.ceu);
      if (Number.isNaN(baseCeu)) {
        this.formValues.ceu = null;
        return;
      }

      if (!req.multiplier) {
        this.formValues.cat2MultiplierValue = null;
        this.formValues.ceu = Number(baseCeu.toFixed(2));
        return;
      }

      const multiplierValue = this.formValues.cat2MultiplierValue;
      const numericMultiplier = multiplierValue === null || multiplierValue === undefined || multiplierValue === ""
          ? null
          : Number(multiplierValue);
      if (numericMultiplier === null || Number.isNaN(numericMultiplier)) {
        this.formValues.ceu = null;
        return;
      }
      this.formValues.ceu = Number((baseCeu * numericMultiplier).toFixed(2));
    },
  },
  watch: {
    "formValues.programTypeId": function (newValue) {
      this.showCourses = false;
      this.showCoursesText = false;
      this.showCeu = false;

      this.formValues.categoryId =
          this.mutableProgramTypeNo === 0 ? this.formValues.categoryId : null;

      if (newValue === 2) {
        this.showTrainingModality = true;
      } else {
        this.showTrainingModality = false;
      }

      this.mutableProgramTypeNo++;
    },
    "formValues.categoryId": async function (newValue) {
      if (this.mutableCategoryNo > 0) {
        this.formValues.courseTitleCat2 = "";
        this.formValues.course = null;
        this.formValues.cpdProviderId = null;
        this.formValues.ceu = null;
        this.formValues.activityDeliveryId = null;
        this.formValues.certificateReasonId = null;
        this.formValues.cat2RequirementId = null;
        this.formValues.cat2Role = null;
        this.formValues.cat2Multiplier = null;
        this.formValues.cat2MultiplierValue = null;
      }

      const selectedCategory =
          this.CATEGORIES.length !== 0
              ? this.CATEGORIES.filter((data) => {
                return data.id === newValue;
              })
              : null;

      this.courseCat =
          selectedCategory.length !== 0 ? selectedCategory[0].categoryType : null;

      if (this.courseCat === "CAT1") {
        this.showCourses = true;
        this.showCoursesText = false;
        this.showCeu = false;
        this.showTrainersNumber = true;
        this.showActivityDelivery = true;
        this.CAT2_REQUIREMENTS = [];

      } else if (this.courseCat === "CAT2") {
        this.showCourses = false;
        this.showCoursesText = true;
        this.showTrainersNumber = false;
        this.showActivityDelivery = false;
        await this.loadCat2Requirements(newValue);
        // If editing, keep previous selection and compute CEU.
        this.applySelectedCat2Requirement();

      } else {
        this.showCourses = false;
        this.showCoursesText = false;
        this.showCeu = false;
        this.CAT2_REQUIREMENTS = [];

      }

      this.mutableCategoryNo++;
    },
    "formValues.cat2RequirementId": function (newVal, oldVal) {
      if (this.courseCat !== "CAT2") return;
      const isInitialHydrate =
          !!this.formValues?.editMode && (oldVal === null || oldVal === undefined);
      if (!isInitialHydrate && newVal !== oldVal) {
        this.formValues.cat2MultiplierValue = null;
      }
      this.applySelectedCat2Requirement();
    },
    "formValues.cat2MultiplierValue": function () {
      if (this.courseCat !== "CAT2") return;
      this.applySelectedCat2Requirement();
    },
    "formValues.course": function (course) {
      if (course) {
        this.showCourseLevel = true;
        this.showPassingScore = true;
        this.courseLevelOptions = this.normalizeCourseLevels(course);
        const selectedLevel =
            this.courseLevelOptions.find((level) => level.id === this.formValues.courseLevelId) ||
            this.courseLevelOptions[0] ||
            null;
        this.formValues.courseLevelId = selectedLevel ? selectedLevel.id : null;
        this.applyCourseLevel(selectedLevel);
        this.ensureEndDateMin();
      } else {
        this.showCourseLevel = false;
        this.showPassingScore = false;
        this.courseLevelOptions = [];
        this.formValues.courseLevelId = null;
        this.applyCourseLevel(null);
        this.ensureEndDateMin();
      }
    },
    "formValues.courseLevelId": function (levelId) {
      if (!this.courseLevelOptions.length) return;
      const selected = this.courseLevelOptions.find((level) => level.id === levelId);
      this.applyCourseLevel(selected);
      this.ensureEndDateMin();
    },
    "formValues.startDate": function (value) {
      if (!value) {
        this.formValues.endDate = "";
        return;
      }
      this.ensureEndDateMin();
    },
  },
};
</script>

<style scoped>
.cpd-activity-form__shell {
  padding: 12px;
}

.cpd-activity-form__section-title {
  margin: 12px 16px 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
}

.cpd-activity-form__grid {
  margin: 12px 16px 6px;
  row-gap: 12px;
}

.cpd-activity-form__grid > div {
  margin-bottom: 0 !important;
}

.cpd-activity-form__grid br {
  display: none;
}

.funder-row {
  margin-bottom: 12px;
  padding: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
}

.event-outcome-field {
  box-sizing: border-box;
  height: 48px;
  min-height: 48px;
  resize: vertical;
}

.cpd-activity-form__actions {
  margin: 8px 16px 16px;
  justify-content: flex-end;
}

.cpd-activity-form__actions .w-full {
  display: flex;
  justify-content: flex-end;
}

.cpd-activity-form__submit {
  background: #1976d2;
  border: 1px solid #1976d2;
  color: #ffffff;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 6px;
}

.cpd-activity-form__submit:hover {
  background: #1565c0;
  border-color: #1565c0;
}

.required > label::after {
  content: "*";
  color: red;
  padding-left: 3px;
}
</style>
